import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Space,
  Switch,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import AdminProductService from "../../../Modules/Services/admin.products.service.ts";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";

const { Option } = Select;

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const categoriesWithProducts = await AdminProductService.getAllProducts();
      const allProducts = categoriesWithProducts.flatMap((category) =>
        category.products.map((product) => ({
          ...product,
          category: {
            category_id: category.category_id,
            category_name: category.category_name,
          },
        }))
      );
      setProducts(allProducts);
    } catch (error) {
      message.error("Ошибка загрузки продуктов");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await AdminCategoryService.getAllCategories();
      setCategories(cats);
    } catch (error) {
      message.error("Не удалось загрузить категории.");
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (editingProduct) {
      form.setFieldsValue({
        name: editingProduct.product_name,
        composition: editingProduct.composition,
        category_id: editingProduct.category.category_id,
        is_stop_list: editingProduct.is_stop_list,
        variants: editingProduct.variants,
      });
      if (editingProduct.image_url) {
        setFileList([
          {
            uid: "-1",
            name: "image.png",
            status: "done",
            url: `http://localhost:3001${editingProduct.image_url}`,
          },
        ]);
      } else {
        setFileList([]);
      }
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [editingProduct, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (key === "variants") {
          formData.append(key, JSON.stringify(values[key]));
        } else {
          formData.append(key, values[key]);
        }
      });

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("image", fileList[0].originFileObj);
      }

      if (editingProduct) {
        await AdminProductService.updateProduct(
          editingProduct.product_id,
          formData
        );
        message.success("Продукт успешно обновлен!");
      } else {
        await AdminProductService.createProduct(formData);
        message.success("Продукт успешно создан!");
      }
      setIsModalVisible(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      message.error("Произошла ошибка.");
      console.error(error);
    }
  };

  const handleToggleStopList = async (product) => {
    try {
      const productData = {
        name: product.product_name,
        category_id: product.category.category_id,
        composition: product.composition,
        image_url: product.image_url,
        is_stop_list: !product.is_stop_list,
      };
      await AdminProductService.updateProduct(product.product_id, productData);
      message.success("Статус стоп-листа обновлен!");
      fetchProducts();
    } catch (error) {
      message.error("Не удалось обновить статус стоп-листа.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await AdminProductService.deleteProduct(id);
      message.success("Продукт успешно удален!");
      fetchProducts();
    } catch (error) {
      message.error("Не удалось удалить продукт.");
    }
  };

  const columns = [
    { title: "ID", dataIndex: "product_id", key: "id" },
    { title: "Название", dataIndex: "product_name", key: "name" },
    {
      title: "Категория",
      dataIndex: ["category", "category_name"],
      key: "category",
    },
    {
      title: "В стоп-листе",
      dataIndex: "is_stop_list",
      key: "is_stop_list",
      render: (is_stop_list) => (is_stop_list ? "Да" : "Нет"),
    },
    {
      title: "Действия",
      key: "actions",
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            style={{ marginRight: 8 }}
            onClick={() => {
              setEditingProduct(record);
              setIsModalVisible(true);
            }}
          >
            Редактировать
          </Button>
          <Button
            style={{ marginRight: 8 }}
            onClick={() => handleToggleStopList(record)}
          >
            {record.is_stop_list ? "Убрать из стоп-листа" : "В стоп-лист"}
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => handleDelete(record.product_id)}
          >
            Удалить
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          setEditingProduct(null);
          setIsModalVisible(true);
        }}
        style={{ marginBottom: 16 }}
      >
        Создать продукт
      </Button>
      <Table
        dataSource={products}
        columns={columns}
        rowKey="product_id"
        loading={loading}
      />
      <Modal
        title={editingProduct ? "Редактировать продукт" : "Создать продукт"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProduct(null);
        }}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="composition"
            label="Состав"
            rules={[{ required: true, message: "Введите состав" }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="is_stop_list"
            label="В стоп-листе"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="category_id"
            label="Категория"
            rules={[{ required: true, message: "Выберите категорию" }]}
          >
            <Select>
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Изображение">
            <Upload
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFileList(fileList)}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Загрузить</Button>
            </Upload>
          </Form.Item>

          <Form.List name="variants">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "variant_id"]}
                      fieldKey={[fieldKey, "variant_id"]}
                      hidden
                    >
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "size"]}
                      fieldKey={[fieldKey, "size"]}
                      rules={[{ required: true, message: "Введите размер" }]}
                    >
                      <Input placeholder="Размер" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "weight"]}
                      fieldKey={[fieldKey, "weight"]}
                      rules={[{ required: true, message: "Введите вес" }]}
                    >
                      <Input placeholder="Вес" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "price"]}
                      fieldKey={[fieldKey, "price"]}
                      rules={[{ required: true, message: "Введите цену" }]}
                    >
                      <Input placeholder="Цена" />
                    </Form.Item>
                    <Button onClick={() => remove(name)}>Удалить</Button>
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Добавить вариант
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageProducts;
